import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { Icons } from '@components/Icons/Icons';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type React from 'react';
import {
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
  useRef,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export const overlayStyles =
  'fixed left-0 top-0 z-3 grid h-screen w-screen place-items-center overflow-y-auto bg-grey-transparent';

export const contentStyles =
  'dialog-content fixed bottom-0 left-0 rounded-2xl right-0 top-0 z-3 mx-auto my-auto flex h-fit max-h-[85vh] w-[90vw] flex-col flex-nowrap items-center justify-center bg-white p-8 shadow-dialog outline-hidden dark:bg-black tiny:w-[80vw] lg:w-[50vw] print:shadow-none overflow-y-auto';

export function Modal({
  children,
  show,
  setShow,
  className,
}: {
  children: ReactNode;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  className?: string;
}): ReactElement {
  return (
    <Dialog.Root open={show} onOpenChange={setShow}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlayStyles} id="overlay" />
        <Dialog.Content
          aria-label="Overlay dialog content"
          aria-describedby={undefined}
          className={twMerge([contentStyles, 'overflow-hidden', className])}
          onInteractOutside={(e) => {
            // password managers sit outside the dialog, so need to check if user has clicked outside the modal to close
            // or if password manager was clicked, then remain open
            const target = e.target;
            if (target instanceof HTMLElement && target.id === 'overlay') {
              e.preventDefault();
              setShow(false);
            }
          }}
        >
          <VisuallyHidden asChild>
            <Dialog.Title>Modal Content</Dialog.Title>
          </VisuallyHidden>
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute right-2.5 top-2.5 border-none bg-transparent font-secondary text-4xl text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:no-underline dark:text-white dark:hover:cursor-pointer dark:hover:text-orange dark:hover:no-underline dark:focus:cursor-pointer dark:focus:no-underline print:hidden"
            >
              <Icons.Close />
            </button>
          </Dialog.Close>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ModalNoPortal({
  children,
  show,
  setShow,
  className,
}: {
  children: ReactNode;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  className?: string;
}): ReactElement | null {
  const contentRef = useRef<HTMLDivElement>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close if the click is actually on the overlay
    // and not within any part of the content
    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      setShow(false);
    }
  };

  return show ? (
    <div
      onClick={handleOverlayClick}
      data-open={show}
      aria-modal="true"
      className={twJoin(overlayStyles, 'modal')}
    >
      <div
        ref={contentRef}
        aria-label="Overlay dialog content"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
        className={mergeStyles(className ?? '', contentStyles)}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => setShow(false)}
          className="absolute right-2.5 top-2.5 border-none bg-transparent font-secondary text-4xl text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:no-underline dark:text-white dark:hover:cursor-pointer dark:hover:text-orange dark:hover:no-underline dark:focus:cursor-pointer dark:focus:no-underline print:hidden"
        >
          <Icons.Close />
        </button>
        {children}
      </div>
    </div>
  ) : null;
}
